// modules/projekty.js — dátové jadro modulu Projekty (zákazky).
// TRETÍ modul cez generic runtime a PRVÝ CONTAINER-based: projekt = container
// module_type='job' (kernel/container.js — kernel ho čakal, tabuľka aj enum
// existujú), úlohy = records module='task' s container_id=job.id.
//
// KĽÚČOVÁ REALITA SCHÉMY: containers tabuľka NEMÁ `custom` stĺpec (len
// parent_id/module_type/label — schema.js DDL) a DDL je zakázané. Metadáta
// projektu (client_ref/deadline/budget_cents) preto žijú v sesterskom recorde
// module='job_meta' (container_id=job.id, polia v records.custom JSON) —
// presne kernel zákon „modul NIKDY nepridáva tabuľku/stĺpec, pridáva `module`
// hodnotu + custom_fields nad records enginom". Container ostáva čistá RELATE
// entita, dáta žijú v records. createJob píše container + meta ATOMICKY
// (db.transaction) — nikdy projekt bez metadát alebo metadáta bez projektu.
//
// Úlohy mapujú rezervované stĺpce kernelu ako dochádzka: qty = odhad hodín,
// unit = 'h', unit_cost_cents = sadzba v centoch, container_id = projekt.
// Stav úlohy (done/billed_by) žije v custom JSON. Hodnota práce = reduce
// úloh (taskValueCents/unbilledWorkCents), NIKDY uložený stav.
//
// MOST K PEŇAZOM (⭐): allTasksDone(job) → buildInvoiceDraftFromJob pripraví
// ČISTÝ draft (žiadny zápis); zápis robí až issueInvoiceFromJobDraft PO user
// potvrdení (risk gate — faktúra NIKDY auto). Položky bez ceny sa NEfabrikujú
// — draft ich vynechá a prizná v skipped_without_price (honest numbers).
//
// 0 fetch / 0 LLM / 0 nových závislostí / 0 DDL — čistá lokálna vrstva.
import { db, newCommonFields } from '../db';
import { isValidIsoDate, todayIso, daysBetweenIso } from '../dateUtils';
import { createInvoice } from '../records';

// ─── MODULE_DEF — deklaratívny descriptor modulu (DÁTA, žiadna logika) ────────
// Rovnaký Š2 tvar ako sklad/dochadzka; NOVÉ je view.source:'containers' —
// zoznam obrazovky renderuje containery (job), nie records. Generic runtime
// (ModuleScreen) sa túto vetvu učí ADITÍVNE; default (undefined) = record view.
export const MODULE_DEF = {
  module: 'projekty', // zosúladené s MODULE_MANIFEST id 'projekty'
  label_sk: 'Projekty',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'green',
  entity: {
    base: 'container', // PRVÝ container-based modul (module_type 'job')
    module_type: 'job',
    custom_fields: [
      { stable_id: 'f_client', label: 'Klient', type: 'text' },
      { stable_id: 'f_deadline', label: 'Termín', type: 'date' },
      { stable_id: 'f_budget', label: 'Rozpočet (€)', type: 'number' },
    ],
  },
  google: { sheet_tab: 'Projekty', form: null, calendar: true },
  triggers: [{ on: 'create', do: 'recomputeJobProgress' }],
  view: { source: 'containers', filter: { module_type: 'job' }, sort: 'created_at_desc' },
  access: { collaborator: true, max_tier_out: 2 },
  metrics: [
    { slug: 'aktivne_projekty', label_sk: 'Aktívne projekty', reduce: 'engine', hero: true },
    { slug: 'nefakturovana_praca', label_sk: 'Nefakturovaná práca', reduce: 'engine', hero: true },
    { slug: 'projekty_v_riziku', label_sk: 'Projekty v riziku', reduce: 'engine', hero: true, alertWhenPositive: true }, // alert: prázdny→grey, 0→green, >0→red (health metrika, ako pod_minimom)
  ],
  signals: [
    { fact: 'projekty.unbilled_work_cents', from_metric: 'nefakturovana_praca', semantic: 'cash_in' },
    { fact: 'projekty.jobs_at_risk', from_metric: 'projekty_v_riziku', semantic: 'risk' },
  ],
  surfaces: [
    {
      // Checklist úloh pre kolaboranta (stub, len DEKLARÁCIA — surface broker
      // príde v surface balíku; honest states).
      surface_id: 'ulohy', kind: 'form_or_html', role: 'kolaborant',
      input: ['task_label', 'done'],
      writes: { module: 'task', reason: 'checklist' },
    },
  ],
  flows: ['flow:projekt-do-faktury@1'],
};

// module hodnoty modulu (closed — reduce filtrujú presne tieto).
export const JOB_META_MODULE = 'job_meta';
export const TASK_MODULE = 'task';

// Deadline okno rizika: projekt s termínom do ≤7 dní (vrátane po termíne)
// a nedokončenými úlohami = v riziku.
export const JOB_RISK_WINDOW_DAYS = 7;

// Celé nezáporné číslo v centoch (peniaze = int centy, nikdy float).
const isCents = (v) => Number.isInteger(v) && v >= 0;
// Konečné nezáporné číslo (hodiny smú byť desatinné, napr. 2.5 h).
const isNonNegNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

// ─── createJob — projekt = container 'job' + sesterský meta record (ATOMICKY) ─
// input = { label (povinné), client_ref?, deadline? (ISO), budget_cents? (int) }
// Container nesie identitu+label (containers NEMÁ custom stĺpec — 0 DDL);
// metadáta žijú v records.custom meta recordu. Jedna db.transaction —
// all-or-nothing (vzor bankMatch.persistAndApplyMatches). Vracia { job, meta }.
//
// Voliteľný `tx` (MOD-PROJ-c, ARCH §8.2 LOCKED = B): keď volajúci UŽ drží engine
// mutex (napr. CRM convertWonDealToProject v db.transaction), ambient db.transaction
// tu = SELF-DEADLOCK (adapter top-level tx je vždy mutex-serializovaná). S tx handle
// píšeme cez tx.transaction (SAVEPOINT na bežiacej tx — žiadny mutex). Bez tx =
// pôvodná ambient vetva, bajt-identická (každý existujúci volajúci ju drží).
export async function createJob(input, tx = null) {
  if (typeof input?.label !== 'string' || input.label.trim() === '') {
    throw new Error('label je povinný');
  }
  if (input.deadline != null && !isValidIsoDate(input.deadline)) {
    throw new Error('deadline musí byť valídny ISO dátum (YYYY-MM-DD)');
  }
  if (input.budget_cents != null && !isCents(input.budget_cents)) {
    throw new Error('budget_cents musí byť celé nezáporné číslo v centoch');
  }

  // Voliteľný deterministický kľúč (stable_key) — pri konverzii won→projekt ho posiela
  // createProjectFromDeal ako deal_id. Dôvod (Codex diera #1, cross-device): dve zariadenia
  // offline skonvertujú ten istý deal; s náhodnými id by vznikli DVA joby aj DVA meta recordy
  // (per-id PK → sync ich nemergne) = dvojitá ZÁKAZKA. Deterministické id z deal_id → obe
  // zariadenia vyrobia ROVNAKÉ job.id aj meta.id → sync ich zlúči (LWW) → práve JEDEN projekt.
  // MUSIA byť deterministické OBA (container aj sesterský meta) — inak meta ostane duplicitný.
  // POZOR: garantuje JEDEN PROJEKT, NIE jednu faktúru — dvaja offline writeri, čo obaja vystavia
  // faktúru (random invoice id) pred syncom, ostanú dvaja (invoice-level dedup = online autorita,
  // samostatná offline TOCTOU limitácia).
  // Validácia striktná (Codex): prítomný stable_key MUSÍ byť neprázdny string bezpečnej abecedy
  // (bez whitespace/apostrofov — inak by rozbil Drive exact-name query a normalizácia by
  // kolidovala s untrimmed deal_id porovnaniami) → radšej throw než ticho spadnúť na random.
  let stable = null;
  if (input.stable_key !== undefined && input.stable_key !== null) {
    if (typeof input.stable_key !== 'string' || !/^[A-Za-z0-9_-]+$/.test(input.stable_key)) {
      throw new Error('stable_key musí byť neprázdny string znakov [A-Za-z0-9_-] (deterministický kľúč)');
    }
    stable = input.stable_key;
  }

  // Container = čistá RELATE entita (tvar kernel/container.js createContainer;
  // inline kvôli atomicite s meta recordom — createContainer by písal mimo tx).
  const job = {
    ...newCommonFields(),
    ...(stable ? { id: `job-${stable}` } : {}),
    parent_id: null,
    module_type: 'job',
    label: input.label.trim(),
  };
  // Sesterský meta record — metadáta VÝHRADNE v custom JSON (top-level pole
  // by zhodilo INSERT, ako records.paid).
  const meta = {
    ...newCommonFields(),
    ...(stable ? { id: `jobmeta-${stable}` } : {}),
    module: JOB_META_MODULE,
    entry_date: todayIso(),
    label: input.label.trim(),
    container_id: job.id, // RELATE na projekt (idx_records_container_id)
    amount_cents: null,
    status: 'confirmed',
    source: 'manual',
    custom: {
      client_ref: input.client_ref || null,
      deadline: input.deadline || null,
      budget_cents: input.budget_cents ?? null,
    },
  };

  const write = async (t) => {
    await t.containers.add(job);
    await t.records.add(meta);
  };
  if (tx) await tx.transaction(write);                              // SAVEPOINT na bežiacej tx — žiadny mutex
  else await db.transaction('rw', db.containers, db.records, write); // ambient vetva — bajt-identická
  return { job, meta };
}

// ─── createJobTask — úloha projektu (module='task', container_id=job.id) ──────
// input = { job_id (povinné), label (povinné), hours_est? (>0, desatinné OK),
//           rate_cents? (int centy), due_date? (ISO), entry_date? (ISO) }
// hours_est/rate_cents → rezervované stĺpce kernelu qty/unit_cost_cents
// (vzor dochadzka createTimeEntry); done/billed_by → custom JSON. Zdieľa
// module='task' s advisor taskmi (records.createTask) — projektová úloha sa
// odlišuje container_id (jobTasks filtruje) + source 'manual'.
// Terminálny stav zákazky. ZÁMERNE inline (nie import z jobLifecycle): jobLifecycle
// importuje z TOHTO modulu (JOB_META_MODULE/allTasksDone) → obojsmerný import je
// krehký load-order cyklus, a toto je money kód. Musí sedieť s
// jobLifecycle.TERMINAL_JOB_STATUSES (['done','cancelled']).
const isJobTerminalStatus = (s) => s === 'done' || s === 'cancelled';

// Chyby modulu Projekty — machine-readable `.code` (UI ich číta: E_JOB_TERMINAL →
// ponúkne jedno-klik reopenJob). projekty.js NEMÁ ProjError (tá žije v jobLifecycle
// — import = load cyklus), preto vlastný ľahký tvar: Error + .code.
function projErr(code, message) {
  const e = new Error(message);
  e.code = code;
  return e;
}

// Čerstvé čítanie statusu zákazky VNÚTRI tx (nositeľ = sesterský ŽIVÝ job_meta).
// Vracia status string | undefined (undefined = ŽIADNY živý job_meta = zákazka
// neexistuje / je zmazaná). undefined ≠ 'draft' — rozlíšenie zatvára fail-open dieru
// (Codex C 2026-07-17: soft-deleted terminál meta → task by prešiel → revival meta
// = done s otvorenou úlohou). Číta cez tx handle — read mimo tx = race.
async function jobStatusInTx(jobId, tx) {
  if (typeof jobId !== 'string' || jobId === '') return undefined;
  const metas = await tx.records
    .filter((r) => r != null && r.module === JOB_META_MODULE && r.container_id === jobId && r.deleted_at == null)
    .toArray();
  if (metas.length === 0) return undefined;
  return metas[0].custom?.status ?? 'draft';
}

export async function createJobTask(input, tx = null) {
  if (typeof input?.job_id !== 'string' || input.job_id.trim() === '') {
    throw new Error('job_id je povinný');
  }
  if (typeof input?.label !== 'string' || input.label.trim() === '') {
    throw new Error('label je povinný');
  }
  if (input.hours_est != null && !(isNonNegNumber(input.hours_est) && input.hours_est > 0)) {
    throw new Error('hours_est musí byť číslo > 0');
  }
  if (input.rate_cents != null && !isCents(input.rate_cents)) {
    throw new Error('rate_cents musí byť celé nezáporné číslo v centoch');
  }
  for (const f of ['due_date', 'entry_date']) {
    if (input[f] != null && !isValidIsoDate(input[f])) {
      throw new Error(`${f} musí byť valídny ISO dátum (YYYY-MM-DD)`);
    }
  }

  const record = {
    ...newCommonFields(),
    module: TASK_MODULE,
    entry_date: input.entry_date || todayIso(),
    due_date: input.due_date || null,
    label: input.label.trim(),
    qty: input.hours_est ?? null, // rezervované stĺpce kernelu ↓
    unit: 'h',
    unit_cost_cents: input.rate_cents ?? null,
    container_id: input.job_id, // RELATE na projekt (idx_records_container_id)
    amount_cents: null,
    status: 'confirmed',
    source: 'manual',
    // done/billed_by — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: {
      done: false,
      billed_by: null,
    },
  };

  // TX-MUTEX fix (P1-ADAPTER-TX-MUTEX): re-read stavu zákazky + zápis úlohy MUSIA
  // byť v JEDNEJ transakcii, inak sa createJobTask vlepí do súbežného
  // transitionJob(→done) okna → done zákazka S OTVORENOU úlohou = falošná faktúra.
  // Fable rozhodnutie 2026-07-17 (A'): done|cancelled je TERMINÁL — pridať úlohu
  // do uzavretej zákazky sa ODMIETNE (bez ohľadu na fakturáciu); user ju musí
  // najprv explicitne otvoriť cez reopenJob (logované do status_history).
  // Voliteľný `tx` (ako createJob, Codex B 2026-07-17): keď volajúci UŽ drží engine
  // mutex (db.transaction), ambient db.transaction = SELF-DEADLOCK → píš cez
  // tx.transaction (SAVEPOINT na bežiacej tx, žiadny mutex).
  const run = async (t) => {
    const status = await jobStatusInTx(input.job_id, t);
    if (status === undefined) {
      throw projErr('E_NOT_FOUND', 'Zákazka neexistuje alebo je zmazaná');
    }
    if (isJobTerminalStatus(status)) {
      throw projErr('E_JOB_TERMINAL', 'Zákazka je uzavretá — otvor ju znova');
    }
    await t.records.add(record);
  };
  if (tx) await tx.transaction(run);
  else await db.transaction(run);
  return record;
}

// ─── setTaskDone — prepnutie stavu úlohy (LWW update, custom merge) ───────────
// records.update nahrádza celý custom stĺpec → merge existujúceho custom je
// povinný (inak by sa stratil billed_by). Fakturovaná úloha sa NEDÁ odškrtnúť
// späť (billed_by set) — faktúra už existuje, spätné „nehotové" by klamalo.
export async function setTaskDone(taskId, done, tx = null) {
  // TX-MUTEX fix (P1-ADAPTER-TX-MUTEX): get→check→update MUSÍ byť atomické (jedna
  // tx), inak sa odškrtnutie vlepí do súbežného transitionJob(→done) okna → done
  // zákazka s odškrtnutou (done=false) úlohou = falošná faktúra / lost update.
  // Voliteľný `tx` (ako createJob, Codex B): volanie zvnútra existujúcej tx nesmie
  // otvoriť ambient db.transaction (self-deadlock) → tx.transaction (SAVEPOINT).
  const run = async (t) => {
    const rec = await t.records.get(taskId);
    if (!rec || rec.module !== TASK_MODULE || rec.deleted_at != null) {
      throw new Error('Úloha neexistuje');
    }
    if (rec.custom?.billed_by && done !== true) {
      throw new Error('Fakturovaná úloha sa nedá vrátiť na nehotovú');
    }
    // Odškrtnutie hotovej úlohy na UZAVRETEJ zákazke sa odmietne (Fable A',
    // 2026-07-17): done|cancelled = terminál, späť len cez explicitný reopenJob.
    // Len odškrtnutie (done!==true) môže porušiť done-invariant; idempotentné
    // zaškrtnutie (done===true) terminál nekontroluje. Ak job_meta chýba
    // (undefined), task je orphan → odškrtnutie neškodné (žiadny transition beží).
    if (done !== true) {
      const status = await jobStatusInTx(rec.container_id, t);
      if (isJobTerminalStatus(status)) {
        throw projErr('E_JOB_TERMINAL', 'Zákazka je uzavretá — otvor ju znova');
      }
    }
    const custom = { ...(rec.custom || {}), done: done === true };
    await t.records.update(taskId, {
      custom,
      updated_at: new Date().toISOString(),
      rev: (rec.rev || 1) + 1,
    });
    return { ...rec, custom };
  };
  return tx ? tx.transaction(run) : db.transaction(run);
}

// ─── Čisté reduce funkcie — stav projektu sa NIKDY neukladá ───────────────────

// Živá úloha projektu (module='task', nezmazaná, s container_id).
const isLiveTask = (r) =>
  r != null && r.module === TASK_MODULE && r.deleted_at == null;

// Živý meta record projektu.
const isLiveJobMeta = (r) =>
  r != null && r.module === JOB_META_MODULE && r.deleted_at == null;

// Úlohy JEDNÉHO projektu. Čistá funkcia nad poľom records — žiadne DB čítanie.
export function jobTasks(records, jobId) {
  const recs = Array.isArray(records) ? records : [];
  if (typeof jobId !== 'string' || jobId === '') return [];
  return recs.filter((r) => isLiveTask(r) && r.container_id === jobId);
}

// Meta record projektu (client_ref/deadline/budget_cents) alebo null.
export function jobMeta(records, jobId) {
  const recs = Array.isArray(records) ? records : [];
  if (typeof jobId !== 'string' || jobId === '') return null;
  return recs.find((r) => isLiveJobMeta(r) && r.container_id === jobId) || null;
}

// Progres projektu: { done, total }. done = úlohy s custom.done === true.
export function jobProgress(records, jobId) {
  const tasks = jobTasks(records, jobId);
  let done = 0;
  for (const t of tasks) {
    if (t.custom?.done === true) done += 1;
  }
  return { done, total: tasks.length };
}

// Všetky úlohy hotové? Projekt BEZ úloh NIE JE hotový (nič sa nespravilo —
// „hotové" bez práce by falošne ponúkalo faktúru).
export function allTasksDone(records, jobId) {
  const { done, total } = jobProgress(records, jobId);
  return total > 0 && done === total;
}

// Hodnota úlohy v centoch = round(hodiny × sadzba). Úloha bez hodín alebo bez
// sadzby prispieva 0 — NIKDY nefabrikuj sumu (honest numbers). Zaokrúhlenie
// RAZ per úloha (qty desatinné, sadzba int centy) — rovnaký per-záznam
// rounding pattern ako sklad/dochadzka.
export function taskValueCents(task) {
  if (task == null) return 0;
  const q = Number(task.qty);
  if (!Number.isFinite(q) || q <= 0) return 0;
  if (!isCents(task.unit_cost_cents)) return 0;
  return Math.round(q * task.unit_cost_cents);
}

// Nefakturovaná hotová práca v centoch = Σ taskValueCents hotových úloh bez
// billed_by — signal cash_in (peniaze čo si user môže vyfakturovať už dnes).
export function unbilledWorkCents(records) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const r of recs) {
    if (!isLiveTask(r)) continue;
    if (r.custom?.done !== true) continue;
    if (r.custom?.billed_by != null) continue;
    cents += taskValueCents(r);
  }
  return cents;
}

// Projekty v riziku: deadline (z meta recordu) do ≤JOB_RISK_WINDOW_DAYS dní
// (vrátane po termíne) A úlohy nedokončené. Projekt bez deadline nikdy
// nefiruje (prah nedeklaroval — žiadny falošný alarm). Vracia
// [{ job_id, label, deadline, days_left, done, total }] zoradené najskorší
// termín najprv (deterministicky, pri zhode podľa job_id).
export function jobsAtRisk(records, today = todayIso()) {
  const recs = Array.isArray(records) ? records : [];
  const out = [];
  for (const m of recs) {
    if (!isLiveJobMeta(m)) continue;
    const jobId = m.container_id;
    if (typeof jobId !== 'string' || jobId === '') continue;
    const deadline = m.custom?.deadline;
    if (!isValidIsoDate(deadline)) continue;
    const daysLeft = daysBetweenIso(today, deadline);
    if (daysLeft == null || daysLeft > JOB_RISK_WINDOW_DAYS) continue;
    const { done, total } = jobProgress(recs, jobId);
    if (total > 0 && done === total) continue; // hotový projekt nie je v riziku
    out.push({ job_id: jobId, label: m.label || null, deadline, days_left: daysLeft, done, total });
  }
  return out.sort(
    (a, b) => a.days_left - b.days_left || (a.job_id < b.job_id ? -1 : 1)
  );
}

// ─── MOST ⭐ · buildInvoiceDraftFromJob — ČISTÁ príprava draftu (0 zápisov) ────
// Z hotových nefakturovaných úloh projektu postaví createInvoice-ready draft.
// Úloha bez ceny (hodiny alebo sadzba chýba) sa NEfabrikuje — vynechá sa
// a prizná v skipped_without_price. Vracia { job_id, items, task_ids,
// total_cents, skipped_without_price, client_ref } alebo null keď nie je čo
// fakturovať. ŽIADNY zápis — zápis robí issueInvoiceFromJobDraft PO potvrdení.
export function buildInvoiceDraftFromJob(records, jobId) {
  const tasks = jobTasks(records, jobId).filter(
    (t) => t.custom?.done === true && t.custom?.billed_by == null
  );
  if (tasks.length === 0) return null;

  const items = [];
  const taskIds = [];
  let skipped = 0;
  let total = 0;
  for (const t of tasks) {
    const cents = taskValueCents(t);
    if (cents <= 0) {
      skipped += 1; // bez ceny → do faktúry nejde (nefabrikuj), ale prizná sa
      continue;
    }
    items.push({
      desc: t.label || 'Práca na projekte',
      qty: Number(t.qty),
      unit: t.unit || 'h',
      unit_price_cents: t.unit_cost_cents,
    });
    taskIds.push(t.id);
    total += cents;
  }
  if (items.length === 0) return null; // všetko bez ceny — draft by bol prázdny

  const meta = jobMeta(records, jobId);
  return {
    job_id: jobId,
    items,
    task_ids: taskIds,
    total_cents: total,
    skipped_without_price: skipped,
    client_ref: meta?.custom?.client_ref || null,
  };
}

// ─── MOST ⭐ · issueInvoiceFromJobDraft — zápis AŽ PO user potvrdení ──────────
// (0) GUARD pred zápisom: čerstvé DB čítanie — projekt už má faktúru
//     (container_id) ALEBO úlohy draftu už majú billed_by? → throw, NIKDY
//     druhá faktúra tej istej práce (double-bill = daňový problém).
// (1) createInvoice (vlastná atomická tx — counter + record; NEVOLAŤ vnútri
//     inej tx, engine mutex by sa zamkol sám na sebe),
// (1b) HNEĎ link faktúra ← container_id=job (post-create update vzorom
//     convertProformaToInvoice — createInvoice nemá generický passthrough
//     a records.js je frozen). Oddelene od billed_by fázy: aj keď fáza 2
//     zlyhá, faktúra NIE JE sirota a guard (0a) ju pri retry nájde.
// (2) follow-up db.transaction: úlohy → custom.billed_by=invoice.id
//     (idempotentné — opakovaný beh prepíše rovnakou invoice.id, žiadna škoda).
// Ak (1b)/(2) zlyhá, faktúra UŽ existuje — throw s jasnou správou (nič
// neprehltni), UI nech usera pošle skontrolovať faktúry; guard pri retry
// zabráni tichému duplikátu.
export async function issueInvoiceFromJobDraft(draft, { counterparty, issue_date, due_date, is_dph_payer } = {}) {
  if (!draft || !Array.isArray(draft.items) || draft.items.length === 0) {
    throw new Error('Draft faktúry je prázdny — nie je čo fakturovať');
  }
  const name = counterparty?.name || draft.client_ref || '';
  if (typeof name !== 'string' || name.trim() === '') {
    throw new Error('Odberateľ (meno klienta) je povinný');
  }

  // (0) Idempotency guard — draft môže byť STALE (starý UI stav, retry po
  // chybe). Over čerstvé records PRED createInvoice:
  // (a) projekt už má nezmazanú faktúru → retry po fáza-2 zlyhaní ju nájde
  //     cez container_id namiesto vystavenia druhej;
  // (b) úloha draftu už má billed_by → tá práca je už vyfakturovaná (chráni
  //     aj keď faktúru user medzitým zmazal a draft je stale).
  // LIMITÁCIA (known): sekvenčný retry/stale draft = pokrytý; SÚBEŽNÝ race
  // (Promise.all / multi-device — dva zápisy prečítajú „0 faktúr" naraz,
  // TOCTOU) guard neuzavrie — skutočný fix je DB unique index job→invoice
  // (DDL, backlog). Guard okno aspoň výrazne zužuje.
  const existingInvoices = await db.records
    .filter(
      (r) =>
        r.module === 'invoice' &&
        r.deleted_at == null &&
        r.container_id === draft.job_id
    )
    .toArray();
  if (existingInvoices.length > 0) {
    throw new Error(
      `Projekt už má faktúru ${existingInvoices[0].number} — skontroluj faktúry`
    );
  }
  for (const taskId of draft.task_ids || []) {
    const t = await db.records.get(taskId);
    if (t && t.deleted_at == null && t.custom?.billed_by != null) {
      throw new Error(
        'Úlohy z tohto návrhu sú už vyfakturované — skontroluj faktúry'
      );
    }
  }

  const invoice = await createInvoice({
    issue_date: issue_date || todayIso(),
    due_date: due_date || null,
    counterparty: { ...(counterparty || {}), name: name.trim() },
    items: draft.items,
    is_dph_payer: is_dph_payer === true,
  });

  // (1b) Okamžitý link faktúra ← projekt (container_id je reálny stĺpec
  // records) — faktúra NIKDY nie je sirota, väzba na projekt vzniká hneď
  // pri vystavení, nie až s billed_by.
  try {
    await db.records.update(invoice.id, {
      container_id: draft.job_id,
      custom: { ...(invoice.custom || {}), job_ref: draft.job_id },
      updated_at: new Date().toISOString(),
      rev: (invoice.rev || 1) + 1,
    });
  } catch (e) {
    console.error('[v2/projekty] faktúra vytvorená, ale link na projekt zlyhal', e);
    throw new Error(
      `Faktúra ${invoice.number} vznikla, ale prepojenie na projekt zlyhalo — skontroluj faktúry pred opakovaním.`
    );
  }

  // (2) Už LEN billed_by na úlohy (custom merge — update nahrádza celý JSON).
  // Idempotentné: ak by bežalo 2×, billed_by sa prepíše rovnakou invoice.id.
  try {
    await db.transaction('rw', db.records, async (tx) => {
      for (const taskId of draft.task_ids) {
        const t = await tx.records.get(taskId);
        if (!t || t.deleted_at != null) continue; // úloha medzitým zmazaná — faktúra platí
        await tx.records.update(taskId, {
          custom: { ...(t.custom || {}), billed_by: invoice.id },
          updated_at: new Date().toISOString(),
          rev: (t.rev || 1) + 1,
        });
      }
    });
  } catch (e) {
    console.error('[v2/projekty] faktúra vytvorená, ale označenie úloh zlyhalo', e);
    throw new Error(
      `Faktúra ${invoice.number} vznikla, ale označenie úloh ako fakturované zlyhalo — skontroluj faktúry pred opakovaním.`
    );
  }
  return invoice;
}

// ─── buildProjektySignals — fakty pre mozog (advisor snapshot) ────────────────
// Deklaratívne: fakty aj semantiky žijú v MODULE_DEF.signals; tu len mapa
// from_metric → výpočet. VŠETKO z records (job_meta+task) — containers netreba,
// snapshot pipeline sa nemení (meta record je join point). Vracia
// { 'projekty.unbilled_work_cents': int (cash_in),
//   'projekty.jobs_at_risk': int (risk) }. Neznámy from_metric = chyba
// descriptora → throw (chyby vypíš, neprehltni).
const METRIC_COMPUTE = {
  nefakturovana_praca: (records, today) => unbilledWorkCents(records),
  projekty_v_riziku: (records, today) => jobsAtRisk(records, today).length,
};

export function buildProjektySignals(records, today = todayIso()) {
  const out = {};
  for (const sig of MODULE_DEF.signals) {
    const compute = METRIC_COMPUTE[sig.from_metric];
    if (!compute) {
      throw new Error(`projekty: signal '${sig.fact}' referencuje neznámu metriku '${sig.from_metric}'`);
    }
    out[sig.fact] = compute(records, today);
  }
  return out;
}
